/**
 * Analytics Tab - Thống kê và phân tích sử dụng AI Assistant
 */

import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Empty, Spin } from 'antd';
import {
  UserOutlined,
  FileTextOutlined,
  QuestionCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import * as aiAssistantService from '../../services/aiAssistantService';

const AnalyticsTab: React.FC = () => {
  const [statistics, setStatistics] = useState<aiAssistantService.AIStatistics | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      const data = await aiAssistantService.getStatistics();
      setStatistics(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!statistics) {
    return (
      <Card>
        <Empty description="Không có dữ liệu thống kê" />
      </Card>
    );
  }

  const topQueriesColumns: ColumnsType<{ query: string; count: number }> = [
    {
      title: 'Câu hỏi',
      dataIndex: 'query',
      key: 'query',
    },
    {
      title: 'Số lượt',
      dataIndex: 'count',
      key: 'count',
      width: 100,
      align: 'center',
    },
  ];

  const recentActivityColumns: ColumnsType<{
    timestamp: string;
    type: string;
    description: string;
  }> = [
    {
      title: 'Thời gian',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString('vi-VN'),
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      width: 120,
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
    },
  ];

  return (
    <div>
      {/* Overview Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng bộ sưu tập"
              value={statistics.totalCollections}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng tài liệu"
              value={statistics.totalDocuments}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng câu hỏi"
              value={statistics.totalQueries}
              prefix={<QuestionCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tỷ lệ thành công"
              value={statistics.successRate}
              precision={1}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Performance Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <Card>
            <Statistic
              title="Thời gian phản hồi trung bình"
              value={statistics.avgResponseTime}
              precision={0}
              suffix="ms"
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Top Queries */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Câu hỏi phổ biến nhất" bordered={false}>
            <Table
              columns={topQueriesColumns}
              dataSource={statistics.topQueries}
              rowKey="query"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>

        {/* Recent Activity */}
        <Col xs={24} lg={12}>
          <Card title="Hoạt động gần đây" bordered={false}>
            <Table
              columns={recentActivityColumns}
              dataSource={statistics.recentActivity}
              rowKey={(record) => record.timestamp + record.type}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AnalyticsTab;
